import React, { useEffect, useState } from "react";
import { NavLink, Outlet, useNavigate, useLocation, useSearchParams } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { DashboardAPI, BuildingAPI } from "../../services/api";
import { clearToken, getUserFromToken } from "../../services/authService";
import LanguageSwitcher from "../../components/LanguageSwitcher";

// Icons for Layout 1 (Admin Main)
import { ReactComponent as DashboardIcon } from "../../assets/icons/layout1/dashboard.svg";
import { ReactComponent as FeedbackIcon } from "../../assets/icons/layout1/feedback.svg";
import { ReactComponent as BuildingIcon } from "../../assets/icons/layout1/building.svg";
import { ReactComponent as QrCodeIcon } from "../../assets/icons/layout1/qrcode.svg";
import { ReactComponent as LogOutIcon } from "../../assets/icons/layout1/logout.svg";

// Icons for Layout 2 (Admin Map Management)
import { ReactComponent as DrawIcon } from "../../assets/icons/layout2/draw.svg";
import { ReactComponent as QrcodeIcon } from "../../assets/icons/layout2/qrcode.svg";
import { ReactComponent as MeetingRoomIcon } from "../../assets/icons/layout2/meetingroom.svg";
import { ReactComponent as AddLocationIcon } from "../../assets/icons/layout2/addlocation.svg";
import { ReactComponent as LineIcon } from "../../assets/icons/layout2/line.svg";
import { ReactComponent as HomeIcon } from "../../assets/icons/layout2/home.svg";

const linkClass = ({ isActive }) =>
  [
    "flex items-center gap-3 px-4 py-3 rounded-xl font-semibold text-sm transition",
    isActive
      ? "bg-blue-600 text-white shadow-sm"
      : "text-slate-500 hover:bg-slate-100 hover:text-slate-700",
  ].join(" ");

export default function AdminLayout() {
  const navigate = useNavigate();
  const location = useLocation();
  const { t } = useTranslation();
  const qs = location.search; // preserve ?buildingId=...&floorId=... across all pages

  const [searchParams] = useSearchParams();
  const buildingId = searchParams.get("buildingId");
  const [buildingName, setBuildingName] = useState("");

  useEffect(() => {
    if (!buildingId) {
      setBuildingName("");
      return;
    }

    let active = true;
    BuildingAPI.get(buildingId)
      .then((b) => {
        if (!active) return;
        if (b && b.name) {
          setBuildingName(b.name);
        } else {
          const mocks = [
            { id: "HPT-001", name: "อาคารเฉลิมพระบารมี" },
            { id: "HPT-002", name: "อาคารสุริโยโน" },
            { id: "HPT-003", name: "อาคารบุญสม มาร์ติน" },
            { id: "HPT-004", name: "ศูนย์ศรีพัฒน์ คณะแพทยศาสตร์" }
          ];
          const found = mocks.find(m => m.id === buildingId);
          setBuildingName(found ? found.name : buildingId);
        }
      })
      .catch(() => {
        if (!active) return;
        const mocks = [
          { id: "HPT-001", name: "อาคารเฉลิมพระบารมี" },
          { id: "HPT-002", name: "อาคารสุริโยโน" },
          { id: "HPT-003", name: "อาคารบุญสม มาร์ติน" },
          { id: "HPT-004", name: "ศูนย์ศรีพัฒน์ คณะแพทยศาสตร์" }
        ];
        const found = mocks.find(m => m.id === buildingId);
        setBuildingName(found ? found.name : buildingId);
      });

    return () => {
      active = false;
    };
  }, [buildingId]);

  const user = getUserFromToken();
  const isAdmin2 = location.pathname.startsWith("/admin2");
  const [feedbackCount, setFeedbackCount] = useState(null);

  useEffect(() => {
    if (isAdmin2) return;

    let active = true;
    DashboardAPI.feedbackStats()
      .then((stats) => {
        if (active) setFeedbackCount(stats?.pending ?? stats?.total ?? 0);
      })
      .catch(() => {
        if (active) setFeedbackCount(null);
      });

    return () => {
      active = false;
    };
  }, [isAdmin2]);

  useEffect(() => {
    if (isAdmin2) return;

    const handleFeedbackStatusUpdated = (event) => {
      const previousStatus = event.detail?.previousStatus;
      const nextStatus = event.detail?.status;

      setFeedbackCount((current) => {
        if (typeof current !== "number") return current;
        if (previousStatus === "pending" && nextStatus === "done") {
          return Math.max(0, current - 1);
        }
        if (previousStatus === "done" && nextStatus === "pending") {
          return current + 1;
        }
        return current;
      });
    };

    window.addEventListener("feedback-status-updated", handleFeedbackStatusUpdated);
    return () => {
      window.removeEventListener("feedback-status-updated", handleFeedbackStatusUpdated);
    };
  }, [isAdmin2]);

  const onSignOut = () => {
    clearToken();
    navigate("/login", { replace: true });
  };

  const onBackToMain = () => {
    navigate("/admin/buildings");
  };

  if (isAdmin2) {
    // Render Layout 2: Map management tools
    return (
      <div className="h-screen bg-slate-50 overflow-hidden">
        <div className="flex h-screen overflow-hidden">
          {/* SIDEBAR NAVIGATION */}
          <aside className="w-[260px] shrink-0 bg-white border-r border-slate-200 px-4 py-5 flex flex-col">
            {/* Admin profile block */}
            <div className="flex items-center gap-3 px-2">
              <div className="w-10 h-10 rounded-full bg-slate-200 overflow-hidden" />
              <div className="min-w-0">
                <div className="font-black text-slate-900 leading-tight">
                  {user?.name || t("admin.layout.admin")}
                </div>
                <div className="text-xs text-slate-500 truncate">
                  {user?.email || ""}
                </div>
              </div>
            </div>

            {/* Navigation menu */}
            <div className="mt-9">
              <div className="px-2 text-[11px] font-black tracking-widest text-slate-400 uppercase">
                {t("admin.layout.mapManagementTools")}
                <div className="px-2 mt-4 text-[11px] font-black tracking-widest text-slate-400 uppercase">
                  {t("admin.layout.boundary")}
                </div>
                <nav className="mt-3 flex flex-col gap-2">
                  <NavLink to={{ pathname: "/admin2", search: qs }} end className={linkClass}>
                    <DrawIcon className="w-4 h-4" />
                    {t("admin.layout.drawMap")}
                  </NavLink>
                </nav>
                <div className="px-2 mt-4 text-[11px] font-black tracking-widest text-slate-400 uppercase">
                  {t("admin.layout.defineAreas")}
                </div>
                <nav className="mt-3 flex flex-col gap-2">
                  <NavLink to={{ pathname: "/admin2/setqr", search: qs }} className={linkClass}>
                    <QrcodeIcon className="w-4 h-4" />
                    {t("admin.layout.setQrArea")}
                  </NavLink>

                  {/* ปิดใช้งาน */}
                  <div
                    className="flex items-center gap-3 px-4 py-3 rounded-xl font-extrabold text-sm text-slate-300 cursor-not-allowed opacity-50 select-none bg-transparent"
                    title={t("admin.layout.pageNotAvailable")}
                  >
                    <MeetingRoomIcon className="w-4 h-4 text-slate-300" />
                    {t("admin.layout.addDestination")}
                  </div>

                  {/* ปิดใช้งาน */}
                  <div
                    className="flex items-center gap-3 px-4 py-3 rounded-xl font-extrabold text-sm text-slate-300 cursor-not-allowed opacity-50 select-none bg-transparent"
                    title={t("admin.layout.pageNotAvailable")}
                  >
                    <AddLocationIcon className="w-5 h-5 text-slate-300" />
                    {t("admin.layout.additionalLocation")}
                  </div>
                </nav>
                <div className="px-2 mt-4 text-[11px] font-black tracking-widest text-slate-400 uppercase">
                  {t("admin.layout.navigation")}
                </div>
                <nav className="mt-3 flex flex-col gap-2">
                  {user?.role === "super_admin" ? (
                    <NavLink to={{ pathname: "/admin2/navigation", search: qs }} className={linkClass}>
                      <LineIcon className="w-4 h-4" />
                      {t("admin.layout.walkingPath")}
                    </NavLink>
                  ) : (
                    <div
                      className="flex items-center gap-3 px-4 py-3 rounded-xl font-extrabold text-sm text-slate-300 cursor-not-allowed opacity-50 select-none bg-transparent"
                      title={t("admin.layout.pageNotAvailable")}
                    >
                      <LineIcon className="w-4 h-4 text-slate-300" />
                      {t("admin.layout.walkingPath")}
                    </div>
                  )}
                </nav>
              </div>
            </div>

            {/* Back to main dashboard button */}
            <button
              onClick={onBackToMain}
              className="mt-auto flex items-center justify-center gap-3 px-2 py-3 text-white bg-blue-950 hover:bg-blue-900 rounded-lg"
            >
              <HomeIcon className="w-4 h-4 items-center justify-center text-center" />
              {t("admin.layout.backToMain")}
            </button>
          </aside>

          {/* MAIN CONTENT AREA */}
          <div className="flex-1 min-w-0 flex flex-col">
            {/* TOP HEADER BAR */}
            <header className="h-14 bg-white border-b border-slate-200 flex items-center justify-between">
              <div className="items-center justify-center rounded-lg px-5 py-auto flex font-black text-slate-700">
                {buildingName ? (
                  <span className="text-xl font-bold text-slate-800">
                    อาคาร : {buildingName}
                  </span>
                ) : (
                  t("admin.layout.building")
                )}
              </div>
              <div className="mr-2">
                <LanguageSwitcher />
              </div>
            </header>

            {/* PAGE CONTENT (router outlet) */}
            <main className="flex-1 min-h-0 flex flex-col overflow-hidden">
              <Outlet />
            </main>
          </div>
        </div>
      </div>
    );
  }

  // Render Layout 1: Dashboard and settings
  return (
    <div className="h-screen bg-slate-50 overflow-hidden">
      <div className="flex h-screen overflow-hidden">
        {/* SIDEBAR NAVIGATION */}
        <aside className="w-[260px] shrink-0 bg-white border-r border-slate-200 px-4 py-5 flex flex-col">
          {/* Admin profile block */}
          <div className="flex items-center gap-3 px-2">
            <div className="w-10 h-10 rounded-full bg-slate-200 overflow-hidden" />
            <div className="min-w-0">
              <div className="font-black text-slate-900 leading-tight">
                {user?.name || t("admin.layout.admin")}
              </div>
              <div className="text-xs text-slate-500 truncate">
                {user?.email || ""}
              </div>
            </div>
          </div>

          {/* Navigation menu */}
          <div className="mt-9">
            <div className="px-2 text-[11px] font-black tracking-widest text-slate-400 uppercase">
              {t("admin.layout.dashboards")}
            </div>

            <nav className="mt-3 flex flex-col gap-2">
              <NavLink to="/admin" end className={linkClass}>
                <DashboardIcon className="w-4 h-4" />
                {t("admin.layout.overview")}
              </NavLink>

              <NavLink to="/admin/feedback" className={linkClass}>
                <FeedbackIcon className="w-4 h-4" />
                <span className="flex-1">{t("admin.layout.feedback")}</span>
                <span className="text-xs font-bold bg-slate-100 text-slate-600 px-2 py-1 rounded-full">
                  {feedbackCount ?? "-"}
                </span>
              </NavLink>
            </nav>

            <div className="mt-9 px-2 text-[11px] font-black tracking-widest text-slate-400 uppercase">
              {t("admin.layout.management")}
            </div>

            <nav className="mt-3 flex flex-col gap-2">
              <NavLink to="/admin/buildings" className={linkClass}>
                <BuildingIcon className="w-4 h-4" />
                {t("admin.layout.buildingSettings")}
              </NavLink>

              <NavLink to="/admin/qr" className={linkClass}>
                <QrCodeIcon className="w-4 h-4" />
                {t("admin.layout.qrCode")}
              </NavLink>
            </nav>
          </div>

          {/* Sign out button */}
          <button
            onClick={onSignOut}
            className="mt-auto flex items-center gap-3 px-2 py-3 text-red-500 font-semibold hover:text-red-600"
          >
            <LogOutIcon className="w-4 h-4" />
            {t("admin.layout.signOut")}
          </button>
        </aside>

        {/* MAIN CONTENT AREA */}
        <div className="flex-1 min-w-0 flex flex-col">
          {/* TOP HEADER BAR */}
          <header className="h-16 bg-white border-b border-slate-200 flex items-center justify-between px-6">
            <div className="font-semibold text-slate-500">
              {buildingName ? (
                <span className="text-xl font-bold text-slate-800">
                  อาคาร : {buildingName}
                </span>
              ) : (
                t("admin.layout.overviewHeader")
              )}
            </div>
            <LanguageSwitcher />
          </header>

          {/* PAGE CONTENT (router outlet) */}
          <main className="p-3 overflow-auto flex-1 min-h-0">
            <Outlet />
          </main>
        </div>
      </div>
    </div>
  );
}
